/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useRef } from 'react';
import { Box, MenuItem, Select, ListItemText } from '@mui/material';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setFilters, setFilterSchema } from '@/features/table/instantSearchSlice';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, getTime } from 'date-fns';
import { useInstantSearchOwnerFilters } from '@/hooks/useInstantSearchOwnerFilters';
import { OverflowChips } from '@/components/OverflowChips';
import { useChipFit } from '@/hooks/useChipFit';

const todayStart = () => getTime(startOfDay(new Date()));
const todayEnd = () => getTime(endOfDay(new Date()));
const weekStart = () => getTime(startOfWeek(new Date(), { weekStartsOn: 1 }));
const weekEnd = () => getTime(endOfWeek(new Date(), { weekStartsOn: 1 }));

const DATE_OPTIONS = [
	{ label: 'Overdue', value: 'overdue' },
	{ label: 'Due Today', value: 'today' },
	{ label: 'Due This Week', value: 'week' },
];

const toFilterString = (value) => {
	switch (value) {
		case 'overdue':
			return `dueDate:<${todayStart()}`;
		case 'today':
			return `dueDate:[${todayStart()}..${todayEnd()}]`;
		case 'week':
			return `dueDate:[${weekStart()}..${weekEnd()}]`;
		default:
			return '';
	}
};

const fromFilterString = (str) => {
	if (!str) return [];
	console.log('DATE OPTIONS', DATE_OPTIONS, str);
	return DATE_OPTIONS.filter(({ value }) => str.includes(toFilterString(value))).map(
		({ value }) => value,
	);
};

const TaskDueSelect = ({ disabled, slug: slugProp, width = '150px' }) => {
	const dispatch = useDispatch();

	const containerRef = useRef();

	const { filterSchema, slug } = useInstantSearchOwnerFilters(slugProp);

	const dueDateFilter = filterSchema?.dueDate ?? '';
	const currentFilters = useMemo(() => fromFilterString(dueDateFilter), [dueDateFilter]);

	const limitTags = useChipFit(containerRef, currentFilters.length);

	const allSelected = DATE_OPTIONS.every((o) => currentFilters.includes(o.value));

	const menuOptions = useMemo(() => {
		const toggleOption = allSelected
			? { label: 'Clear Filters', value: 'Clear' }
			: { label: 'All', value: 'All' };
		return [toggleOption, ...DATE_OPTIONS];
	}, [allSelected]);

	const handleChange = (event) => {
		const vals = event.target.value;

		// Check for empty/null first to avoid errors
		if (!vals || vals.length === 0 || vals.includes('Clear')) {
			dispatch(setFilterSchema({ slug, type: 'dueDate', value: '' }));
			return;
		}

		if (vals.includes('All')) {
			const combined = `(${DATE_OPTIONS.map(({ value }) => toFilterString(value)).join(' || ')})`;
			dispatch(setFilterSchema({ slug, type: 'dueDate', value: combined }));
			return;
		}

		// Get unique values from selection
		const uniqueValues = [...new Set(vals)];
		const filterStrings = uniqueValues.map(toFilterString).filter(Boolean);
		const combined =
			filterStrings.length === 1 ? filterStrings[0] : `(${filterStrings.join(' || ')})`;

		dispatch(setFilterSchema({ slug, type: 'dueDate', value: combined }));
	};

	return (
		<Box width={width}>
			<Select
				ref={containerRef}
				multiple
				fullWidth
				disabled={disabled}
				sx={{
					display: 'flex',
					maxHeight: 35,
					alignItems: 'center',
				}}
				value={currentFilters}
				onChange={handleChange}
				renderValue={(selected) => (
					<OverflowChips
						items={selected}
						limitTags={limitTags}
						className='refinement-list-item'
						getLabel={(v) => DATE_OPTIONS.find((o) => o.value === v)?.label}
					/>
				)}
			>
				{menuOptions.map(({ value, label }) => {
					const isSelected =
						(value === 'All' && allSelected) || currentFilters.includes(value);
					return (
						<MenuItem key={`date-select-${value}`} value={value}>
							{isSelected ? <CheckBox /> : <CheckBoxOutlineBlank />}
							<ListItemText primary={label} />
						</MenuItem>
					);
				})}
			</Select>
		</Box>
	);
};

export default TaskDueSelect;
